import React from 'react';
import { TextField, Stack } from '@mui/material';
import Grid2 from '@mui/material/Grid2';

const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
    },
};

/**
 * Address + contact fields for an external business. Shared between the
 * user-creation flow and the user-edit flow so both render the exact same
 * inputs in the same order.
 *
 * Props:
 *   values         { businessName, email, phoneNumber, streetAddress, city,
 *                    province, postalCode, country }
 *   onChange       (field, value) => void - for text fields
 *   onPhoneChange  (field, value) => void - digit-only handler for phone
 *                                            (defaults to onChange)
 *   errors         optional { field: string } error map
 *   showEmail      include the email field (default true)
 *   required       mark all visible fields required (default true)
 */
const BusinessFormFields = ({
    values,
    onChange,
    onPhoneChange,
    errors = {},
    showEmail = true,
    required = true,
}) => {
    const phoneHandler = onPhoneChange || onChange;
    const text = (field, label, extra = {}) => (
        <TextField
            fullWidth
            size="small"
            label={label}
            value={values[field] || ''}
            onChange={(e) => onChange(field, e.target.value)}
            error={!!errors[field]}
            helperText={errors[field]}
            required={required}
            sx={FIELD_SX}
            {...extra}
        />
    );

    return (
        <Stack spacing={2}>
            {text('businessName', 'Business Name')}
            {showEmail ? (
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        {text('email', 'Business Email', { type: 'email' })}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Business Phone"
                            value={values.phoneNumber || ''}
                            onChange={(e) => phoneHandler('phoneNumber', e.target.value)}
                            placeholder="(123) 456-7890"
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber}
                            required={required}
                            sx={FIELD_SX}
                        />
                    </Grid2>
                </Grid2>
            ) : (
                <TextField
                    fullWidth
                    size="small"
                    label="Business Phone"
                    value={values.phoneNumber || ''}
                    onChange={(e) => phoneHandler('phoneNumber', e.target.value)}
                    placeholder="(123) 456-7890"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    required={required}
                    sx={FIELD_SX}
                />
            )}
            {text('streetAddress', 'Street Address')}
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>{text('city', 'City')}</Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>{text('province', 'Province')}</Grid2>
            </Grid2>
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>{text('postalCode', 'Postal Code')}</Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>{text('country', 'Country')}</Grid2>
            </Grid2>
        </Stack>
    );
};

export default BusinessFormFields;
